import { useState } from "react";
import Switch from "react-switch";
import Select from "react-select";
import AutoCompleteField from "../../components/field-auto-complete";
import { expandMatterNumber, formatMatterLabelFromData, formatMatterNumber } from "../../utils/matter-display";
import { travelTypeRequiresMatter } from "../../utils/mileage-validation";

const REQUIRED_MARK = <span className="text-danger" aria-hidden="true"> *</span>;

// Defined at module scope so React never sees it as a new component on re-render.
// Defining Row inside the component body causes inputs to lose focus on every keystroke.
const Row = ({ label, required, children, isMobile }) =>
    isMobile ? (
        <div className="mb-4">
            <div className="mb-2 font-weight-medium">
                {label}{required ? REQUIRED_MARK : null}
            </div>
            <div>{children}</div>
        </div>
    ) : (
        <div className="d-flex pb-2">
            <span className="report-label col-3">
                {label}{required ? REQUIRED_MARK : null}
            </span>
            <div className="col-7">{children}</div>
        </div>
    );

/**
 * Mileage expense creation form — shared by desktop and mobile.
 * Desktop: label + field side by side (report-label col-3 / col-7).
 * Mobile: label stacked above field (full width).
 * The modal container in mileage-list.js handles the overlay sizing per screen size.
 */
const MileageForm = ({
    data,
    form,
    isMobile = false,
    travelTypes = [],
    travelTypesLoading = false,
    travelTypesError = false,
    officeOptions = [],
    officesLoading = false,
    onFieldChange,
    onSubmit,
    onCancel,
}) => {
    const [formErrors, setFormErrors] = useState([]);

    const selectedTravelType = travelTypes.find(tt => tt.id === form.travelTypeId) ?? null;
    const needsMatter = travelTypeRequiresMatter(selectedTravelType);
    const isOfficeTravel = selectedTravelType?.travel_mode === 'office_travel';
    // XPEN-3246-009: a third mode — typed amount, no route. "Not office" no
    // longer means "address"; check this explicitly, the same trap XPEN-3246-008
    // found in the expense editor for this exact travel_mode field.
    const isOpenAmountTravel = selectedTravelType?.travel_mode === 'open_amount';

    const t = (key, fallback = key) => data.t.read(key) || fallback;

    const handleSubmit = () => {
        const errors = [];
        if (!form.travelTypeId)
            errors.push(t('mileage_step1_validation_travel_type', 'Please select a travel type.'));
        if (!isOpenAmountTravel && !form.departure)
            errors.push(isOfficeTravel
                ? t('mileage_step1_validation_departure_office', 'Please select a departure office.')
                : t('mileage_step1_validation_departure_address', 'Please enter a departure address.'));
        if (!isOpenAmountTravel && !form.destination)
            errors.push(isOfficeTravel
                ? t('mileage_step1_validation_destination_office', 'Please select a destination office.')
                : t('mileage_step1_validation_destination_address', 'Please enter a destination address.'));
        if (!isOpenAmountTravel && form.departure && form.destination && form.departure === form.destination)
            errors.push(isOfficeTravel
                ? t('error_mileage_same_office', 'Departure and destination must be different offices.')
                : t('mileage_step1_validation_same_address', 'Departure and destination addresses must be different.'));
        if (isOpenAmountTravel) {
            // This form validates its own flat state (translated sentences), not
            // an expense json — no shared rule with mileage-validation.js's
            // equivalent open_amount check (XPEN-3246-007); keep the threshold
            // (> 0) in sync with it by convention, not by a shared call.
            const amount = Number(form.openAmount);
            if (!Number.isFinite(amount) || amount <= 0) {
                errors.push(t('mileage_step1_validation_amount', 'Please enter the amount you spent.'));
            }
        }
        if (needsMatter && !form.matterId)
            errors.push(t('mileage_step1_validation_matter', 'Please select a matter for this trip.'));
        if (!form.description)
            errors.push(t('mileage_step1_validation_description', 'Please enter a description.'));
        if (errors.length > 0) {
            setFormErrors(errors);
            return;
        }
        setFormErrors([]);
        onSubmit();
    };

    const handleFieldChange = (key, value) => {
        if (formErrors.length > 0) setFormErrors([]);
        onFieldChange(key, value);
    };

    const title = t('mileage_step1_title_new', 'New Mileage Expense');

    return (
        <div
            className={`mileage-form${isMobile ? ' p-4 mileage-form--mobile' : ' p-4'}`}
            data-testid="mileage-form"
        >
            <div style={isMobile ? { maxWidth: 480, margin: '0 auto' } : {}}>
            <h3 className="mb-4">{title}</h3>

            {/* 1 — Travel type */}
            <Row label={t('mileage_step1_travel_type', 'Travel Type')} required isMobile={isMobile}>
                {travelTypesError ? (
                    <p className="text-danger small">{t('failed_to_fetch_rates', 'Failed to load. Please refresh.')}</p>
                ) : travelTypesLoading ? (
                    <p className="text-muted small">…</p>
                ) : travelTypes.length === 0 ? (
                    <p className="text-muted small">{t('mileage_step1_no_travel_types', 'No active travel types configured.')}</p>
                ) : (() => {
                    const travelTypeOptions = travelTypes.map(tt => ({ value: tt.id, label: tt.name }));
                    return (
                        <Select
                            inputId="mileage-form-travel-type"
                            classNamePrefix="select"
                            value={travelTypeOptions.find(o => o.value === form.travelTypeId) ?? null}
                            options={travelTypeOptions}
                            onChange={(opt) => handleFieldChange('travelTypeId', opt?.value ?? '')}
                            placeholder={t('mileage_step1_select_travel_type', '— Select a travel type —')}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    );
                })()}
            </Row>

            {/* 2 — Departure (Open Amount has no route: departure/destination/round-trip do not apply) */}
            {!isOpenAmountTravel && (
                <Row
                    label={isOfficeTravel ? t('mileage_type1.departure_office', 'Departure office') : t('address_origin', 'Departure address')}
                    required
                    isMobile={isMobile}
                >
                    {isOfficeTravel ? (
                        officesLoading ? (
                            <p className="text-muted small">…</p>
                        ) : (
                            <Select
                                inputId="step1-departure"
                                classNamePrefix="select"
                                value={officeOptions.find(o => o.value === form.departure) ?? null}
                                options={officeOptions}
                                onChange={(opt) => handleFieldChange('departure', opt?.value ?? '')}
                                placeholder={t('mileage_type1.departure_office', 'Departure office')}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        )
                    ) : (
                        <AutoCompleteField
                            value={form.departure || ''}
                            onChange={(val) => handleFieldChange('departure', val.name ?? val)}
                            className="form-control"
                            searchUrl="/places/search/"
                            minLength={2}
                            max_results={6}
                            placeholder={t('address_origin', 'Departure address')}
                            data-testid="step1-departure"
                        />
                    )}
                </Row>
            )}

            {/* 3 — Destination */}
            {!isOpenAmountTravel && (
                <Row
                    label={isOfficeTravel ? t('mileage_type1.destination_office', 'Destination office') : t('address_destination', 'Destination address')}
                    required
                    isMobile={isMobile}
                >
                    {isOfficeTravel ? (
                        officesLoading ? (
                            <p className="text-muted small">…</p>
                        ) : (
                            <Select
                                inputId="step1-destination"
                                classNamePrefix="select"
                                value={officeOptions.find(o => o.value === form.destination) ?? null}
                                options={officeOptions}
                                onChange={(opt) => handleFieldChange('destination', opt?.value ?? '')}
                                placeholder={t('mileage_type1.destination_office', 'Destination office')}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        )
                    ) : (
                        <AutoCompleteField
                            value={form.destination || ''}
                            onChange={(val) => handleFieldChange('destination', val.name ?? val)}
                            className="form-control"
                            searchUrl="/places/search/"
                            minLength={2}
                            max_results={6}
                            placeholder={t('address_destination', 'Destination address')}
                            data-testid="step1-destination"
                        />
                    )}
                </Row>
            )}

            {/* 4 — Round trip */}
            {!isOpenAmountTravel && (
                <Row label={t('round_trip', 'Round trip')} isMobile={isMobile}>
                    <Switch
                        checked={!!form.roundTrip}
                        onChange={(val) => handleFieldChange('roundTrip', val)}
                        width={62}
                        height={28}
                        onColor="#F39800"
                        onHandleColor="#ffffff"
                        offColor="#D1D5DB"
                        boxShadow="0 1px 3px rgba(15, 23, 42, 0.18)"
                        activeBoxShadow="0 0 0 2px rgba(243, 152, 0, 0.15)"
                        id="mileage-form-round-trip-switch"
                        data-testid="step1-round-trip"
                    />
                </Row>
            )}

            {/* 4b — Amount (Open Amount only) */}
            {isOpenAmountTravel && (
                <Row label={t('mileage_step1_amount', 'Amount')} required isMobile={isMobile}>
                    <input
                        type="number"
                        className="form-control"
                        value={form.openAmount || ''}
                        onChange={(e) => handleFieldChange('openAmount', e.target.value)}
                        placeholder={t('mileage_step1_amount', 'Amount')}
                        data-testid="step1-amount"
                    />
                </Row>
            )}

            {/* 5 — Matter (conditional: Client scope, billed or not) */}
            {needsMatter && (
                <Row label={t('invoice.matter_id', 'Matter')} required isMobile={isMobile}>
                    <AutoCompleteField
                        value={form.matterId || ''}
                        onChange={(val) => handleFieldChange('matterId', val.name ?? val)}
                        className="form-control"
                        searchUrl="/matters/search/"
                        // XPEN-3322: the picker edits the collapsed number; the full one is
                        // restored before it is stored, so MatterId still resolves at export.
                        formatEditValue={formatMatterNumber}
                        parseEditValue={expandMatterNumber}
                        minLength={1}
                        max_results={6}
                        placeholder={t('invoice.matter_id', 'Matter')}
                        data-testid="step1-matter"
                        formatLabel={(m) => formatMatterLabelFromData(data, m)}
                    />
                </Row>
            )}

            {/* 6 — Work date */}
            <Row label={t('invoice.work_date', 'Work Date')} isMobile={isMobile}>
                <input
                    type="date"
                    className="form-control"
                    value={form.workDate || ''}
                    onChange={(e) => handleFieldChange('workDate', e.target.value)}
                    data-testid="step1-work-date"
                />
            </Row>

            {/* 7 — Description */}
            <Row label={t('add_description', 'Description')} required isMobile={isMobile}>
                <input
                    type="text"
                    className="form-control"
                    value={form.description || ''}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    placeholder={t('invoice.description', 'Description')}
                    data-testid="step1-description"
                />
            </Row>

            {/* Validation alert */}
            {formErrors.length > 0 && (
                <div className="alert alert-danger mt-3 mb-0" role="alert" data-testid="step1-form-errors">
                    <ul className="mb-0 pl-3">
                        {formErrors.map((msg, i) => <li key={i}>{msg}</li>)}
                    </ul>
                </div>
            )}

            {/* Footer buttons */}
            <div className={`d-flex ${isMobile ? 'justify-content-center' : 'justify-content-end mt-4'}`} style={isMobile ? { marginTop: '3.5rem' } : {}}>
                <button
                    type="button"
                    className={`btn btn-secondary ${isMobile ? 'btn-lg mx-3 px-5' : 'mr-2'}`}
                    onClick={onCancel}
                    data-testid="step1-cancel"
                >
                    {t('cancel', 'Cancel')}
                </button>
                <button
                    type="button"
                    className={`btn btn-primary ${isMobile ? 'btn-lg mx-3 px-5' : ''}`}
                    onClick={handleSubmit}
                    data-testid="step1-submit"
                >
                    {t('mileage_step1_save', 'Save')}
                </button>
            </div>
            </div>
        </div>
    );
};

export default MileageForm;
